import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { routes } from "../../lib/urls";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import type { SpaceTile } from "./storageUsageLevel";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";

// The scoped storage rows, each keyed to the bucket it’s drawn under: its
// space, or the folded personal bucket.
export function useStorageUsage({
  pollStorage = false,
}: { pollStorage?: boolean } = {}) {
  const { report, loading } = useMonitoringWorkflows({ pollStorage });
  const { spaces, spaceById, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();

  const foldPersonal = foldsPersonal(scope);
  const bucketOf = (spaceId: string) =>
    foldPersonal && personalIds.has(spaceId) ? PERSONAL_BUCKET : spaceId;
  const labelOf = (bucket: string) => {
    if (bucket === PERSONAL_BUCKET) return "Personal";
    const space = spaceById.get(bucket);
    return space && displayName(space);
  };
  const colorMap = spaceColorMap(spaces);
  colorMap.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
  const spaceTile: SpaceTile = (spaceId) => {
    const key = bucketOf(spaceId);
    return {
      key,
      label: labelOf(key) ?? "Unknown space",
      color: colorMap.get(key) ?? "var(--gray-600)",
    };
  };
  const rows = (report.storageUsage ?? []).filter(
    (row) =>
      matchesSpace(row.spaceId) && labelOf(bucketOf(row.spaceId)) !== undefined
  );

  return {
    loading: loading || spacesLoading,
    rows,
    spaceTile,
  };
}
